import React, { useMemo, useState } from "react";
import PageHeader from "../components/ui/PageHeader.jsx";
import AppButton from "../components/ui/AppButton.jsx";
import {
  detailPageHeaderNavClassName,
  detailPageHeaderNavTouchClassName,
  segmentTabChipButtonClassName,
  segmentTabChipButtonMobileClassName,
  mobileOverflowGuardClassName,
} from "../design/tokens.js";
import { DateOnlyField } from "../components/ui/DatePickers.jsx";
import { Input, Select, Textarea } from "../components/forms/shared/FormAtoms.jsx";
import PillBadge from "../components/ui/PillBadge.jsx";
import ResponsiveDataView from "../components/ui/ResponsiveDataView.jsx";
import ResponsivePageContainer from "../components/ui/ResponsivePageContainer.jsx";
import ResponsiveFormLayout from "../components/ui/ResponsiveFormLayout.jsx";
import MobileActionBar from "../components/ui/MobileActionBar.jsx";
import FilterBarOrDrawer from "../components/ui/FilterBarOrDrawer.jsx";
import LeitstandMobileList from "../components/Übersicht/LeitstandMobileList.jsx";
import StateCard from "../components/ui/StateCard.jsx";
import ModalOrSheet from "../components/ui/ModalOrSheet.jsx";

export default function UiGuardrails() {
  const [date, setDate] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");

  const tableRows = useMemo(
    () => [
      { id: 1, bereich: "Projekt", eintrag: "Stundenrapport", status: "Aktiv", owner: "M. Muster" },
      { id: 2, bereich: "Service", eintrag: "Messwerte", status: "In Arbeit", owner: "S. Test" },
      { id: 3, bereich: "Kunden", eintrag: "Dokumente", status: "Offen", owner: "A. Beispiel" },
    ],
    []
  );

  const tableColumns = useMemo(
    () => [
      { key: "bereich", label: "Bereich" },
      { key: "eintrag", label: "Eintrag" },
      { key: "status", label: "Status" },
      { key: "owner", label: "Verantwortlich" },
    ],
    []
  );

  return (
    <div className={`min-h-screen w-full bg-app text-primary ${mobileOverflowGuardClassName}`}>
      <ResponsivePageContainer maxWidth="5xl" className="space-y-6">
        <div className="rounded-2xl px-4 py-3 border border-default bg-surface">
          <PageHeader
            left={
              <div className="min-w-0">
                <h1 className="text-2xl font-bold truncate">Musterkatalog Enterprise Dark</h1>
                <p className="mt-1 text-sm text-muted truncate">
                  Freigabeseite für zentrale UI-Muster (Dark-only).
                </p>
              </div>
            }
            right={
              <>
                <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Zurück</AppButton>
                <AppButton type="button" variant="secondary" size="lg" className={detailPageHeaderNavClassName}>Dashboard</AppButton>
                <AppButton type="button" variant="primary" size="lg" className={detailPageHeaderNavTouchClassName}>Freigeben</AppButton>
              </>
            }
          />
        </div>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Dark Referenz (Abnahme zuerst)</h2>
          <div className="rounded-xl border border-default bg-surface-elevated p-3 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <AppButton
                variant="primary"
                className="border-blue-400/70 shadow-[0_0_0_1px_rgba(59,130,246,0.35)]"
              >
                Neue Regel
              </AppButton>
              <AppButton variant="secondary">Bearbeiten</AppButton>
              <AppButton variant="danger">Löschen</AppButton>
            </div>
            <div className="text-xs text-muted">
              Freigabeziel Dark: ruhige Flächen, klare Trennung, CTA sichtbar ohne Neon-Look.
            </div>
          </div>
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Buttons</h2>
          <div className="flex flex-wrap gap-2">
            <AppButton variant="primary">Primary</AppButton>
            <AppButton variant="secondary">Secondary</AppButton>
            <AppButton variant="ghost">Ghost</AppButton>
            <AppButton variant="danger">Danger</AppButton>
            <AppButton variant="success">Success</AppButton>
            <AppButton variant="primary" disabled>Disabled</AppButton>
          </div>
          <div className="text-xs text-muted">
            Abnahme: Größe, Gewicht, Hover, Focus, Disabled müssen in beiden Themes gleichwertig wirken.
          </div>
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Aktionen (Bearbeiten / Löschen / PDF)</h2>
          <div className="flex flex-wrap gap-2">
            <AppButton variant="secondary">Bearbeiten</AppButton>
            <AppButton variant="danger">Löschen</AppButton>
            <AppButton variant="secondary">PDF</AppButton>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-muted">
            <span>Empfehlung: kompakt nebeneinander bei Platz.</span>
            <span>Fallback: gestapelt bei schmalen Spalten oder langen Labels.</span>
          </div>
          <div className="rounded-xl border border-default bg-surface-elevated p-3">
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-sm">
              <div className="text-muted font-semibold">Aktion</div>
              <div />
              <div />
              {Array.from({ length: 4 }).map((_, i) => (
                <React.Fragment key={`action-row-${i}`}>
                  <div className="text-primary self-center">Eintrag {i + 1}</div>
                  <AppButton variant="secondary" size="sm" className="!min-h-[28px] !h-[28px] !px-2 !py-0.5 text-[11px] rounded-md">Bearbeiten</AppButton>
                  <AppButton variant="danger" size="sm" className="!min-h-[28px] !h-[28px] !px-2 !py-0.5 text-[11px] rounded-md">Löschen</AppButton>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-default bg-surface-elevated p-3">
            <div className="grid grid-cols-[1fr_auto] gap-2 text-sm">
              <div className="text-muted font-semibold">Aktion (gestapelt)</div>
              <div />
              {Array.from({ length: 2 }).map((_, i) => (
                <React.Fragment key={`action-stack-${i}`}>
                  <div className="text-primary self-center">Eintrag S{i + 1}</div>
                  <div className="flex flex-col items-end gap-1.5">
                    <AppButton variant="secondary" size="sm" className="min-w-[80px] !min-h-[28px] !h-[28px] !px-2 !py-0.5 text-[11px] rounded-md justify-center">Bearbeiten</AppButton>
                    <AppButton variant="danger" size="sm" className="min-w-[80px] !min-h-[28px] !h-[28px] !px-2 !py-0.5 text-[11px] rounded-md justify-center">Löschen</AppButton>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Mobile Foundation (Phase 2)</h2>
          <p className="text-sm text-muted">
            Abnahme unter 390px (Mobile), 768px (Tablet) und 1280px (Desktop). Desktop-Komponenten ab md unverändert.
          </p>

          <div className="rounded-xl border border-default bg-surface-elevated p-3 space-y-3">
            <h3 className="text-sm font-semibold text-primary">Segment-Chips (Desktop kompakt / Mobile Touch)</h3>
            <div className="flex flex-wrap gap-2">
              <AppButton
                type="button"
                variant="secondary"
                size="sm"
                className={`border-white/15 bg-white/5 ${segmentTabChipButtonClassName}`}
              >
                Nur Desktop-Token
              </AppButton>
              <AppButton
                type="button"
                variant="secondary"
                size="sm"
                className={`border-blue-400/40 bg-blue-500/15 ${segmentTabChipButtonClassName} ${segmentTabChipButtonMobileClassName}`}
              >
                + Mobile-Token
              </AppButton>
            </div>
          </div>

          <div className="rounded-xl border border-default bg-surface-elevated p-3 space-y-3">
            <h3 className="text-sm font-semibold text-primary">FilterBarOrDrawer</h3>
            <FilterBarOrDrawer title="Filter Demo" drawerUntil="xl">
              <AppButton type="button" variant="secondary" size="sm">
                Status
              </AppButton>
              <AppButton type="button" variant="secondary" size="sm">
                Typ
              </AppButton>
              <Input placeholder="Suche…" className="max-w-xs" />
            </FilterBarOrDrawer>
          </div>

          <div className="rounded-xl border border-default bg-surface-elevated p-3 space-y-2 md:hidden">
            <h3 className="text-sm font-semibold text-primary">LeitstandMobileList (nur &lt; md)</h3>
            <LeitstandMobileList
              columns={["Schritt 1", "Schritt 2", "Schritt 3"]}
              rows={[
                { id: 1, name: "Projekt Alpha", cells: [2, 1, 0], next_label: "Rechnung" },
                { id: 2, name: "Service Beta", cells: [1, 0, 0], next_label: "Messung" },
              ]}
            />
          </div>

          <div className="rounded-xl border border-dashed border-default bg-surface-elevated p-3 pb-24 relative min-h-[8rem]">
            <h3 className="text-sm font-semibold text-primary mb-2">MobileActionBar (nur &lt; md sichtbar)</h3>
            <p className="text-xs text-muted mb-3">Am unteren Rand sticky mit Safe-Area.</p>
            <MobileActionBar bleedClassName="">
              <AppButton type="button" variant="success" className="w-full min-h-[44px]">
                Speichern (Demo)
              </AppButton>
            </MobileActionBar>
          </div>
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Formulare</h2>
          <ResponsiveFormLayout>
            <Input placeholder="Textfeld / Suche / Name" />
            <Select defaultValue="">
              <option value="" disabled>
                Auswahl treffen
              </option>
              <option value="a">Option A</option>
              <option value="b">Option B</option>
            </Select>
            <div className="md:col-span-2">
              <Textarea rows={3} placeholder="Mehrzeiliger Text..." />
            </div>
          </ResponsiveFormLayout>
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Suche / Filter / Status-Leiste</h2>
          <div className="rounded-xl border border-default bg-surface-elevated p-3 space-y-3">
            <div className="relative">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className=""
                placeholder="Suche nach Nummer, Kunde, Adresse, Status..."
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <AppButton variant="secondary" size="sm">Filter</AppButton>
              <PillBadge label="Status: Offen" tone="info" />
              <PillBadge label="Typ: Elektro" tone="neutral" />
              <PillBadge label="Priorität: Hoch" tone="warning" />
              <AppButton variant="ghost" size="sm">Filter zurücksetzen</AppButton>
            </div>
            <div className="h-2 rounded-full overflow-hidden bg-muted">
              <div className="h-full bg-green-500 transition-[width] duration-300 ease-out" style={{ width: "62%" }} />
            </div>
            <div className="text-xs text-muted">62 / 100 erledigt (62%)</div>
          </div>
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Badges / Status</h2>
          <div className="flex flex-wrap gap-2">
            <PillBadge label="Neutral" tone="neutral" />
            <PillBadge label="Info" tone="info" />
            <PillBadge label="Success" tone="success" />
            <PillBadge label="Warning" tone="warning" />
            <PillBadge label="Offen" toneClassName="bg-blue-500/15 text-blue-700 border-blue-400/30" />
            <PillBadge label="In Arbeit" toneClassName="bg-amber-500/15 text-amber-700 border-amber-400/30" />
            <PillBadge label="Abgeschlossen" toneClassName="bg-green-500/15 text-green-700 border-green-400/30" />
            <PillBadge label="Storniert" toneClassName="bg-red-500/15 text-red-700 border-red-400/30" />
          </div>
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Tabelle / List-View</h2>
          <div className="flex flex-wrap gap-2">
            <AppButton variant="secondary" size="sm">Alle öffnen</AppButton>
            <AppButton variant="secondary" size="sm">Alle schliessen</AppButton>
            <AppButton variant="secondary" size="sm">Sortieren</AppButton>
            <AppButton variant="ghost" size="sm">Spalten wählen</AppButton>
          </div>
          <ResponsiveDataView data={tableRows} columns={tableColumns} />
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Action-Bereich (Header und Bulk)</h2>
          <div className="rounded-xl border border-default bg-surface-elevated p-3 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-muted">3 Einträge ausgewählt</div>
              <div className="flex flex-wrap gap-2">
                <AppButton variant="secondary" size="sm">Zuweisen</AppButton>
                <AppButton variant="secondary" size="sm">Status setzen</AppButton>
                <AppButton variant="secondary" size="sm">Archivieren</AppButton>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <AppButton variant="primary" size="sm">Neu erstellen</AppButton>
              <AppButton variant="secondary" size="sm">Import</AppButton>
              <AppButton variant="secondary" size="sm">PDF</AppButton>
            </div>
          </div>
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Dark Abnahme Checkliste</h2>
          <div className="grid md:grid-cols-2 gap-2 text-sm">
            <div className="rounded-lg border border-default bg-surface-elevated px-3 py-2">CTA klar erkennbar</div>
            <div className="rounded-lg border border-default bg-surface-elevated px-3 py-2">Danger eindeutig und ruhig</div>
            <div className="rounded-lg border border-default bg-surface-elevated px-3 py-2">Hover und Focus sichtbar</div>
            <div className="rounded-lg border border-default bg-surface-elevated px-3 py-2">Rows/Divider sauber getrennt</div>
            <div className="rounded-lg border border-default bg-surface-elevated px-3 py-2">PDF/Mess-PDF als Sekundäraktion</div>
            <div className="rounded-lg border border-default bg-surface-elevated px-3 py-2">Keine unruhigen Neon-Farben</div>
          </div>
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">States / Empty / Error</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <StateCard state="empty" message="Noch keine Einträge vorhanden." />
            <StateCard state="error" message="Fehler beim Laden der Daten." onRetry={() => {}} />
          </div>
        </section>

        <section className="relative z-40 rounded-2xl p-4 border border-default bg-surface space-y-3 max-w-md">
          <h2 className="text-lg font-semibold">Datum / Picker</h2>
          <DateOnlyField
            label="Datum"
            value={date}
            onChange={setDate}
            placeholder="Datum wählen..."
          />
        </section>

        <section className="rounded-2xl p-4 border border-default bg-surface space-y-3">
          <h2 className="text-lg font-semibold">Modal</h2>
          <AppButton variant="secondary" onClick={() => setModalOpen(true)}>
            Modal öffnen
          </AppButton>
          <div className="flex flex-wrap gap-2">
            <AppButton variant="danger">Löschen bestätigen</AppButton>
            <AppButton variant="secondary">Abbrechen</AppButton>
            <AppButton variant="primary">Speichern</AppButton>
          </div>
        </section>

        <ModalOrSheet
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Muster-Modal"
          footer={
            <div className="flex justify-end gap-2">
              <AppButton variant="secondary" onClick={() => setModalOpen(false)}>Abbrechen</AppButton>
              <AppButton variant="primary" onClick={() => setModalOpen(false)}>Bestätigen</AppButton>
            </div>
          }
        >
          <p className="text-sm text-muted">
            Prüfe hier Overlay, Titel, Content-Fläche, Footer und Button-Hierarchie in beiden Themes.
          </p>
        </ModalOrSheet>
      </ResponsivePageContainer>
    </div>
  );
}
